import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { queryKeys, queryOptions } from '../react-query-config'
import {
  activateReseller,
  createReseller,
  deactivateReseller,
  deleteReseller,
  getReseller,
  getResellers,
  getResellerStats,
  getResellerUsage,
  suspendReseller,
  updateReseller,
  updateResellerBilling,
} from '../resellers'
import type { UpdateResellerDTO, ResellerFilters } from '../types'

// Get all resellers hook
export const useResellers = (filters?: ResellerFilters, options?: { enabled?: boolean }) => {
  const memoizedFilters = useMemo(() => filters, [filters ? JSON.stringify(filters) : null])
  const { enabled, ...restOptions } = options || {}

  return useQuery({
    queryKey: queryKeys.resellers.list(memoizedFilters),
    queryFn: () => getResellers(memoizedFilters),
    ...queryOptions.resellers,
    enabled: enabled !== false, // Default to true if not specified
    ...restOptions,
  })
}

// Get specific reseller hook
export const useReseller = (id: string) => {
  return useQuery({
    queryKey: queryKeys.resellers.detail(id),
    queryFn: () => getReseller(id),
    ...queryOptions.resellers,
    enabled: !!id,
  })
}

// Get reseller stats hook
export const useResellerStats = () => {
  return useQuery({
    queryKey: queryKeys.resellers.stats(),
    queryFn: getResellerStats,
    ...queryOptions.resellers,
  })
}

// Get reseller usage hook
export const useResellerUsage = (id: string) => {
  return useQuery({
    queryKey: queryKeys.resellers.usage(id),
    queryFn: () => getResellerUsage(id),
    ...queryOptions.resellers,
    enabled: !!id,
  })
}

// Create reseller hook
export const useCreateReseller = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createReseller,
    onSuccess: () => {
      // Invalidate and refetch resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.stats() })
    },
  })
}

// Update reseller hook
export const useUpdateReseller = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateResellerDTO }) =>
      updateReseller(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific reseller
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.detail(id) })
      // Invalidate resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
    },
  })
}

// Delete reseller hook
export const useDeleteReseller = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteReseller,
    onSuccess: () => {
      // Invalidate resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.stats() })
    },
  })
}

// Activate reseller hook
export const useActivateReseller = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: activateReseller,
    onSuccess: (_, id) => {
      // Invalidate specific reseller
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.detail(id) })
      // Invalidate resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
    },
  })
}

// Deactivate reseller hook
export const useDeactivateReseller = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deactivateReseller,
    onSuccess: (_, id) => {
      // Invalidate specific reseller
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.detail(id) })
      // Invalidate resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
    },
  })
}

// Suspend reseller hook
export const useSuspendReseller = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: suspendReseller,
    onSuccess: (_, id) => {
      // Invalidate specific reseller
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.detail(id) })
      // Invalidate resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
    },
  })
}

// Update reseller billing hook
export const useUpdateResellerBilling = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: {
        isBillingManaged: boolean
        storagePricePerGbCents?: number
        apiCallPriceCents?: number
      }
    }) => updateResellerBilling(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific reseller
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.detail(id) })
      // Invalidate resellers list
      queryClient.invalidateQueries({ queryKey: queryKeys.resellers.all })
    },
  })
}
